import { Spin } from 'antd';
import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import type { GridCellProps } from 'react-virtualized';

import { KpiCellStack } from './PackArchitectureTableCells';
import { PackArchitectureTableEllipsisText } from './PackArchitectureTableEllipsisText';
import {
    VirtualizedTableShell,
    VIRTUAL_TABLE_HEADER_HEIGHT_PX,
} from './PackArchitectureTableVirtualized.styles';
import { getSegmentCellClassName, getVendorCellClassName } from './packArchitectureTable.columns';
import {
    TABLE_SEGMENT_COLUMN_WIDTH_PX,
    TABLE_VENDOR_COLUMN_WIDTH_PX,
} from './packArchitectureTable.helpers';
import type { TableColumn, TableRow } from './packArchitectureTable.helpers';

type PackArchitectureVirtualizedTableProps = {
    rowHeightPx: number;
    totalTableHeight: number;
    isAppendingPages: boolean;
    visibleColumns: TableColumn[];
    filteredRows: TableRow[];
    hoveredRowKey: string | null;
    hoveredColumnKey: string | null;
    orderedSelectedKpis: string[];
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    yAxisKpiId: string;
    matchesKpiId: (cellKpiId: string, requestedKpiId: string) => boolean;
    clearHighlight: () => void;
    handleColumnHeaderHover: (columnKey: string) => void;
    handleCellHover: (rowKey: string, columnKey: string | null) => void;
    handleRowClick: (row: TableRow) => void;
    handleTableCellClick: (segmentKey: string, columnKey: string) => void;
    getRowClassName: (row: TableRow) => string;
};

type PackArchitectureMultiGridProps = {
    multiGridRef: MutableRefObject<MultiGrid | null>;
    totalTableHeight: number;
    visibleColumns: TableColumn[];
    tableRowCount: number;
    tableColumnCount: number;
    getRowHeight: ({ index }: { index: number }) => number;
    cellRenderer: (props: GridCellProps) => ReactNode;
};

const PackArchitectureMultiGrid = ({
    multiGridRef,
    totalTableHeight,
    visibleColumns,
    tableRowCount,
    tableColumnCount,
    getRowHeight,
    cellRenderer,
}: PackArchitectureMultiGridProps) => (
    <AutoSizer disableHeight>
        {({ width }) => {
            const safeWidth = Math.max(0, width);
            const vendorColumnCount = Math.max(0, visibleColumns.length);
            const minTableContentWidth =
                TABLE_SEGMENT_COLUMN_WIDTH_PX + vendorColumnCount * TABLE_VENDOR_COLUMN_WIDTH_PX;
            const extraWidthPerVendorColumn =
                vendorColumnCount > 0 && safeWidth > minTableContentWidth
                    ? (safeWidth - minTableContentWidth) / vendorColumnCount
                    : 0;
            const resolvedVendorColumnWidth = TABLE_VENDOR_COLUMN_WIDTH_PX + extraWidthPerVendorColumn;
            const getColumnWidth = ({ index }: { index: number }) =>
                index === 0 ? TABLE_SEGMENT_COLUMN_WIDTH_PX : resolvedVendorColumnWidth;

            return (
                <MultiGrid
                    ref={multiGridRef}
                    width={safeWidth}
                    height={totalTableHeight}
                    rowCount={tableRowCount}
                    columnCount={tableColumnCount}
                    fixedRowCount={1}
                    fixedColumnCount={1}
                    rowHeight={getRowHeight}
                    columnWidth={getColumnWidth}
                    overscanRowCount={6}
                    overscanColumnCount={2}
                    classNameTopLeftGrid="pack-arch-vtable-header-left ant-table-header"
                    classNameTopRightGrid="pack-arch-vtable-header-scroll ant-table-header ant-table-content"
                    classNameBottomLeftGrid="pack-arch-vtable-left"
                    classNameBottomRightGrid="pack-arch-vtable-grid ant-table-body"
                    styleTopLeftGrid={{ outline: 'none' }}
                    styleTopRightGrid={{ outline: 'none' }}
                    styleBottomLeftGrid={{ outline: 'none' }}
                    styleBottomRightGrid={{ outline: 'none' }}
                    cellRenderer={cellRenderer}
                />
            );
        }}
    </AutoSizer>
);

const PackArchitectureVirtualizedTable = ({
    rowHeightPx,
    totalTableHeight,
    isAppendingPages,
    visibleColumns,
    filteredRows,
    hoveredRowKey,
    hoveredColumnKey,
    orderedSelectedKpis,
    activeSegmentKey,
    selectedColumnKey,
    yAxisKpiId,
    matchesKpiId,
    clearHighlight,
    handleColumnHeaderHover,
    handleCellHover,
    handleRowClick,
    handleTableCellClick,
    getRowClassName,
}: PackArchitectureVirtualizedTableProps) => {
    const multiGridRef = useRef<MultiGrid | null>(null);
    const tableColumnCount = visibleColumns.length + 1;
    const tableRowCount = filteredRows.length + 1;

    const getRowHeight = useCallback(
        ({ index }: { index: number }) => (index === 0 ? VIRTUAL_TABLE_HEADER_HEIGHT_PX : rowHeightPx),
        [rowHeightPx],
    );

    useEffect(() => {
        if (!multiGridRef.current) return;
        multiGridRef.current.recomputeGridSize();
        multiGridRef.current.forceUpdateGrids();
    }, [rowHeightPx, tableColumnCount, tableRowCount]);

    const cellRenderer = useCallback(
        ({ key, rowIndex, columnIndex, style }: GridCellProps) => {
            const isHeaderRow = rowIndex === 0;
            const isSegmentColumn = columnIndex === 0;

            if (isHeaderRow) {
                if (isSegmentColumn) {
                    return (
                        <div
                            key={key}
                            style={style}
                            className="pack-arch-vtable-header-cell segment"
                            onClick={(event) => {
                                event.stopPropagation();
                                clearHighlight();
                            }}
                        />
                    );
                }

                const column = visibleColumns[columnIndex - 1];
                if (!column) return null;

                return (
                    <div
                        key={key}
                        style={style}
                        className={`pack-arch-vtable-header-cell vendor ${
                            hoveredColumnKey === column.key ? 'table-header-hover-column' : ''
                        }`}
                        onMouseEnter={() => handleColumnHeaderHover(column.key)}
                        onClick={(event) => {
                            event.stopPropagation();
                            clearHighlight();
                        }}
                    >
                        <PackArchitectureTableEllipsisText className="text-left font-semibold">
                            {column.label}
                        </PackArchitectureTableEllipsisText>
                    </div>
                );
            }

            const row = filteredRows[rowIndex - 1];
            if (!row) return null;

            const rowClassName = getRowClassName(row);
            if (isSegmentColumn) {
                return (
                    <div
                        key={key}
                        style={style}
                        data-row-key={row.key}
                        className={`pack-arch-vtable-cell segment ${rowClassName} ${
                            getSegmentCellClassName(row, activeSegmentKey, hoveredRowKey) ?? ''
                        }`}
                        onMouseEnter={() => handleCellHover(row.key, null)}
                        onClick={() => handleRowClick(row)}
                    >
                        <PackArchitectureTableEllipsisText className="w-full text-left text-xs font-semibold">
                            {row.label}
                        </PackArchitectureTableEllipsisText>
                    </div>
                );
            }

            const column = visibleColumns[columnIndex - 1];
            if (!column) return null;

            return (
                <div
                    key={key}
                    style={style}
                    data-row-key={row.key}
                    className={`pack-arch-vtable-cell ${rowClassName} ${getVendorCellClassName(
                        row,
                        column.key,
                        orderedSelectedKpis,
                        activeSegmentKey,
                        selectedColumnKey,
                        hoveredRowKey,
                        hoveredColumnKey,
                        matchesKpiId,
                    )}`}
                    onMouseEnter={() => handleCellHover(row.key, column.key)}
                    onClick={(event) => {
                        event.stopPropagation();
                        handleTableCellClick(row.key, column.key);
                    }}
                >
                    <KpiCellStack
                        cell={row.cells[column.key]}
                        selectedKpis={orderedSelectedKpis}
                        yAxisKpiId={yAxisKpiId}
                        matchesKpiId={matchesKpiId}
                    />
                </div>
            );
        },
        [
            activeSegmentKey,
            clearHighlight,
            filteredRows,
            getRowClassName,
            handleCellHover,
            handleColumnHeaderHover,
            handleRowClick,
            handleTableCellClick,
            hoveredColumnKey,
            hoveredRowKey,
            matchesKpiId,
            orderedSelectedKpis,
            selectedColumnKey,
            visibleColumns,
            yAxisKpiId,
        ],
    );

    return (
        <VirtualizedTableShell $rowHeight={rowHeightPx}>
            <div className="relative" style={{ height: totalTableHeight }}>
                {isAppendingPages ? (
                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/35">
                        <Spin size="small" />
                    </div>
                ) : null}
                <PackArchitectureMultiGrid
                    multiGridRef={multiGridRef}
                    totalTableHeight={totalTableHeight}
                    visibleColumns={visibleColumns}
                    tableRowCount={tableRowCount}
                    tableColumnCount={tableColumnCount}
                    getRowHeight={getRowHeight}
                    cellRenderer={cellRenderer}
                />
            </div>
        </VirtualizedTableShell>
    );
};

export default PackArchitectureVirtualizedTable;
